import * as React from "react";
import { useRef, useState } from "react";
import { WorkflowListItem } from "./types";
import { toShortDate } from "@/helpers/dates";
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
  Toggle,
  PrimaryBlueButton,
  SecondaryGrayButton,
  Modal
} from "@velaro/velaro-component-lib";
import IconCopy from "@/icons/IconCopy";
import IconEdit1 from "@/icons/IconEdit1";
import IconTrash from "@/icons/IconTrash";
import Tooltip from "@/components/Tooltip";
import { useNavigate } from "react-router-dom";

export function WorkflowItem({
  item,
  isLast,
  toggle,
  remove,
  duplicate
}: {
  item: WorkflowListItem;
  isLast: boolean;
  toggle: () => void;
  remove: () => void;
  duplicate?: () => void;
}) {
  const [showConfirmRemoveModal, setShowConfirmRemoveModal] = useState(false);
  const dateCreated = new Date(item.dateCreated || "");
  const lastModified = new Date(item.lastModified || "");
  const dotsRef = useRef<SVGSVGElement>(null);
  const className = isLast ? "" : "border-b border-slate-200";
  const navigate = useNavigate();

  function renderActionBar() {
    return (
      <div className="flex flex-row gap-4 items-center text-slate-400">
        <Tooltip text="Edit" direction="top" minWidth="50px">
          <div
            className="hover:cursor-pointer hover:text-cornflower-blue-500"
            onClick={() => navigate(`${item.id}`)}
          >
            <IconEdit1 />
          </div>
        </Tooltip>
        <Tooltip text="Copy" direction="top" minWidth="50px">
          <div
            className="hover:cursor-pointer hover:text-cornflower-blue-500"
            onClick={() => {
              if (duplicate) duplicate();
            }}
          >
            <IconCopy />
          </div>
        </Tooltip>
        <Tooltip text="Delete" direction="top" minWidth="60px">
          <div
            className="hover:cursor-pointer hover:text-cornflower-blue-500"
            onClick={() => setShowConfirmRemoveModal(true)}
          >
            <IconTrash />
          </div>
        </Tooltip>
      </div>
    );
  }

  return (
    <tr className={`${className} h-12 hover:bg-slate-100`}>
      <td>
        <div className="flex items-center">
          <Checkbox />
        </div>
      </td>
      <td>{item.name}</td>
      <td>{item.createdBy}</td>
      <td>{toShortDate(lastModified)}</td>
      <td className="flex items-center h-12">
        <Toggle
          label={item.enabled ? "Published" : "Draft"}
          toggled={item.enabled}
          changeToggle={toggle}
        />
      </td>
      <td>
        <div className="relative flex justify-end pr-4">
          {renderActionBar()}
          <Modal show={showConfirmRemoveModal}>
            <ModalHeader
              title="Confirm Remove Workflow"
              onClose={() => setShowConfirmRemoveModal(false)}
            />
            <ModalBody>
              <div className="m-4">
                Are you sure you want to remove this workflow?
              </div>
            </ModalBody>
            <ModalFooter>
              <div className="mx-4 my-2 flex justify-between items-center">
                <SecondaryGrayButton
                  label={"Cancel"}
                  onClick={() => setShowConfirmRemoveModal(false)}
                />
                <PrimaryBlueButton
                  label={"Remove"}
                  onClick={() => {
                    setShowConfirmRemoveModal(false);
                    remove();
                  }}
                />
              </div>
            </ModalFooter>
          </Modal>
        </div>
      </td>
    </tr>
  );
}

export function Checkbox(props: React.HTMLProps<HTMLInputElement>) {
  return (
    <input
      className={
        "w-4 h-4 bg-white rounded-3xl border border-slate-300 justify-center items-center gap-2 inline-flex"
      }
      {...props}
      type="checkbox"
    />
  );
}
